import { createContext } from "react";
import { Preference } from "./preference";
import { StorageInterface } from "./storage.interface";
import { DefaultLogger } from "../../logger";
import { StorageAdapterInterface } from "./storage-adapter.interface";
import { LoggerService } from "../logger";

export const StorageServiceContext = createContext<StorageService>(null as any);

export class StorageService implements StorageInterface {
  public context = StorageServiceContext;

  private preferences: { [key: string]: Preference<any> } = {};

  constructor(
    private storageAdapter: StorageAdapterInterface,
    private secureStorageAdapter: StorageAdapterInterface,
    private loggerService: LoggerService
  ) {}

  public getPreference = <T = string | undefined>(
    key: string,
    initialValue?: T
  ): Preference<T> => {
    let pref = this.preferences[key];

    if (!pref) {
      pref = new Preference<T>(key, this, initialValue);
      this.preferences[key] = pref;
    }

    return pref;
  };

  /*
   * Fetches key and passes the result to callback, along with an Error if there is any.
   */
  public getItem(key: string): string | null {
    return this.storageAdapter.getItem(key);
  }

  /**
   * Sets value for key and calls callback on completion, along with an Error if there is any
   */
  public setItem(key: string, value: string): void {
    try {
      return this.storageAdapter.setItem(key, value);
    } catch (e) {
      if (e instanceof DOMException && e.name === "QuotaExceededError") {
        console.error(e);
        this._clearSessionsAfterError();
        try {
          this.storageAdapter.setItem(key, value);
        } catch (e) {
          // If we still get an error, we can't do anything about it
        }
      }
      this.loggerService.captureException(e);
    }
  }

  /**
   * This function is should be called when the local storage is full.
   * We remove all the old session data from local storage to make room for new data
   */
  private _clearSessionsAfterError(): void {
    const arr = []; // Array to hold the keys
    for (let i = 0; i < this.storageAdapter.length; i++) {
      if (this.storageAdapter.key(i)?.startsWith("@gamesession/data")) {
        arr.push(this.storageAdapter.key(i));
      }
    }

    for (const key of arr) {
      if (key) this.storageAdapter.removeItem(key);
    }
  }

  public removeItem(key: string): void {
    return this.storageAdapter.removeItem(key);
  }

  /*
   * Fetches key and passes the result to callback, along with an Error if there is any.
   */
  public getSecureItem = async (key: string): Promise<string | null> => {
    try {
      const value = await this.secureStorageAdapter.getItem(key);

      if (value) return value;
    } catch (e) {
      DefaultLogger.captureException(e);
    }

    return this.getItem(key);
  };

  public migrateInsecureToSecure = async (key: string) => {
    try {
      const value = await this.getItem(key);
      if (value) await this.setSecureItem(key, value);
      await this.removeItem(key);
    } catch (e) {
      DefaultLogger.captureException(e);
    }
  };

  /**
   * Sets value for key and calls callback on completion, along with an Error if there is any
   */
  public setSecureItem = async (key: string, value: string): Promise<void> => {
    try {
      await this.secureStorageAdapter.setItem(key, value);
    } catch (e) {
      DefaultLogger.captureException(e);
      return this.setItem(key, value);
    }
  };

  public removeSecureItem = async (key: string): Promise<void> => {
    try {
      await this.secureStorageAdapter.removeItem(key);
    } catch (e) {
      DefaultLogger.captureException(e);
      return this.removeItem(key);
    }
  };
}
